<template>
  <div :class="[`col-span-${header.width}`]" v-bind="customRowBindings">
    <div
      :class="{
        'flex flex-row justify-between items-center h-full': true,
        border_right: addSideBorder,
      }"
    >
      <template v-if="customColumnRender[header.key]">
        <component
          :is="getCustomComponent"
          :value="row[header.key]"
          v-bind="getComponentProps"
          :row="row"
          :extra-data="extraData"
          v-on="customListeners"
          @update="$emit('update', $event)"
          @refresh="$emit('refresh', $event)"
          class="justify-center"
        />
      </template>
      <template v-else>
        <div class="table_data">
          <img v-if="hasPrefixImage" :src="getPrefixImage" alt="prefix" class="prefix_image" :style="{ borderRadius: prefixImageBorderRadius }" />
          <div class="flex flex-row gap-2 items-center truncate">
            <ProfileIcon v-if="showProfileIcon" :data="row" :avatar="header.avatar" :view="header.view" :param="header.key" />
            <a-tooltip v-if="!isExcludedHeader && !isDescriptionColumn && getCellText" :title="getCellText" placement="top">
              <span class="truncate">
                <span v-if="header.html" v-html="getCellText"></span>
                <span v-else>{{ getCellText }}</span>
              </span>
            </a-tooltip>
            <div v-if="isStatusColumn" :class="['truncate flex flex-row items-center gap-1 text-[13px]', 'ongoing_status', statusProvider[getCellText]?.[1]]">
              <img class="size-2" :src="imagePath.whiteDot" alt="white_dot" />
              {{ statusProvider[getCellText]?.[0] }}
            </div>
          </div>
          <div v-if="isTagsColumn" class="tags">
            <span v-for="(tag, tagIndex) in visibleTags(row[header.key])" :key="tagIndex" class="tag">{{ tag }}</span>
            <span v-if="remainingTagsCount(row[header.key])" class="proxima_nova text-[11px] secondary_color font-normal">+{{ remainingTagsCount(row[header.key]) }}</span>
          </div>
          <div v-if="isDescriptionColumn" class="flex flex-col gap-[2px] truncate">
            <a-tooltip v-if="row[header.key]?.name" :title="row[header.key].name" placement="top">
              <span class="text-sm font-semibold truncate">{{ row[header.key].name }}</span>
            </a-tooltip>
            <a-tooltip v-if="row[header.key]?.description" :title="row[header.key].description" placement="top">
              <span class="text-sm font-normal secondary_color leading-4 truncate">{{ row[header.key].description }}</span>
            </a-tooltip>
          </div>
          <img v-if="hasSuffixImage" :src="row[header.key]?.suffixImage" alt="suffix" class="suffix_image" />
          <component :is="suffixComponent" v-if="showSuffixComponent" />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import imagePath from '@/javascript/imagePath';
import ProfileIcon from '@/components/custom/ProfileIcon.vue';
import { statusProvider, excludedHeaders } from '@/constants/tableConstants.js';

const props = defineProps({
  header: { type: Object, required: true },
  row: { type: Object, required: true },
  colIndex: { type: Number, required: true },
  customColumnRender: { type: Object, default: () => ({}) },
  emptyCellValue: { type: String, default: '' },
  addSideBorder: { type: Boolean, default: false },
  customRow: { type: Function },
  prefixImageBorderRadius: { type: String, default: '50%' },
  suffixComponent: { type: Object, required: false },
  suffixKey: { type: String, required: false },
  source: { type: String, default: '' },
  descriptionKey: { type: String, required: false },
  extraData: { type: Object, required: false, default: null },
});

const emit = defineEmits(['update', 'refresh']);

const getComponentProps = computed(() => {
  const customRender = props.customColumnRender[props.header.key];
  const defaultProps = { value: props.row[props.header.key] };

  if (!customRender?.props) {
    return defaultProps;
  }

  return typeof customRender.props === 'function' ? customRender.props(props.row) : defaultProps;
});

const customRowBindings = computed(() => {
  if (props.headers?.length !== props.colIndex && props.customRow) {
    return props.customRow(props.row, props.colIndex);
  }
  return {};
});

const getCustomComponent = computed(() => {
  const customRender = props.customColumnRender[props.header.key];
  return customRender.component || customRender;
});

const customListeners = computed(() => {
  const customRender = props.customColumnRender[props.header.key];
  return customRender.listeners || {};
});

const hasPrefixImage = computed(() => {
  return (props.row[props.header.key]?.prefixImage || (props.header.prefix && props.row[props.header.prefix])) && props.row[props.header.key];
});

const getPrefixImage = computed(() => {
  return props.row[props.header.key]?.prefixImage || props.row[props.header.prefix];
});

const showProfileIcon = computed(() => {
  return (props.header.view || props.header.avatar) && props.row[props.header.key];
});

const isExcludedHeader = computed(() => {
  return excludedHeaders.includes(props.header.key);
});

const isDescriptionColumn = computed(() => {
  return props.descriptionKey && props.header.key === props.descriptionKey && props.row[props.header.key];
});

const isStatusColumn = computed(() => {
  return props.header.key === 'status';
});

const isTagsColumn = computed(() => {
  return props.header.key === 'tags';
});

const getCellText = computed(() => {
  if (props.emptyCellValue !== '' && props.row[props.header.key] === '') {
    return props.emptyCellValue;
  }
  return props.row[props.header.key]?.text || props.row[props.header.key];
});

const hasSuffixImage = computed(() => {
  return props.row[props.header.key]?.suffixImage;
});

const showSuffixComponent = computed(() => {
  return props.suffixKey && props.header.suffix && props.row[props.suffixKey];
});

const visibleTags = (tags) => {
  const maxTagsToShow = 2;
  return tags?.slice(0, maxTagsToShow) || [];
};

const remainingTagsCount = (tags) => {
  const maxTagsToShow = 2;
  return tags?.length > maxTagsToShow ? tags.length - maxTagsToShow : 0;
};
</script>

<style scoped>
.table_data {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 16px 12px;
  align-self: stretch;
  overflow: hidden;
  color: var(--foreground-rgb);
  text-overflow: ellipsis;
  font-family: 'Proxima Nova', sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
}

.prefix_image,
.suffix_image {
  width: 16px;
  height: 16px;
  border-radius: 50%;
}

.tags {
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

.tag {
  display: flex;
  padding: 3px 8px;
  align-items: center;
  gap: 6px;
  border-radius: 16px;
  border: 1px solid var(--white-opacity-8);
  background: var(--white-opacity-14);
  color: var(--foreground-rgb);
  text-overflow: ellipsis;
  font-family: 'Proxima Nova', sans-serif;
  font-size: 11px;
  font-style: normal;
  font-weight: 400;
}

.ongoing_status {
  padding: 4px 8px;
  border-radius: 24px;
  font-weight: 600;
  cursor: pointer;
}

.default,
.in_active,
.unpaid {
  background: var(--default-icon);
}

.cancelled {
  background: var(--color-orange);
}

.paused {
  background: var(--paused-icon);
}

.in_progress {
  background: #a05bd5;
}

.ended,
.overdue {
  background: var(--ended-icon);
}

.complete,
.active,
.paid {
  background: #2d6711;
}

.researching {
  background: var(--researching-icon);
}

.generating {
  background: #877ae1;
}

.to_review {
  background: #3773e4;
}

.replied {
  background: #118456;
}

.removed {
  background: #ac1c1e;
}

.bounced {
  background: var(--bounced-icon);
}

.unsubscribed {
  background: #ac1c1e;
}
</style>
